// src/components/Contact.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageContext.js';
import { Mail, Phone, ChevronDown } from 'lucide-react';

const Contact = () => {
  const { t } = useLanguage();
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [visibleElements, setVisibleElements] = useState({});

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleElements(prev => ({
            ...prev,
            [entry.target.dataset.id]: true
          }));
        }
      });
    }, observerOptions);

    document.querySelectorAll('[data-observe]').forEach(element => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const faqs = [
    {
      question: t('contact', 'faq1_question'),
      answer: t('contact', 'faq1_answer')
    },
    {
      question: t('contact', 'faq2_question'),
      answer: t('contact', 'faq2_answer')
    },
    {
      question: t('contact', 'faq3_question'),
      answer: t('contact', 'faq3_answer')
    },
    {
      question: t('contact', 'faq4_question'),
      answer: t('contact', 'faq4_answer')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <section id="contact" style={{ backgroundColor: '#FFFFFF' }} className="relative w-full overflow-hidden py-20 md:py-32">
      <div className="container mx-auto px-6">
        
        {/* Header */}
        <div 
          className="max-w-3xl mx-auto text-center mb-16 md:mb-24"
          data-observe
          data-id="contact-header"
        >
          <div 
            className={`transition-all duration-700 ${
              visibleElements['contact-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#585856' }}>
              {t('contact', 'section_subtitle')}
            </p>
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight mb-6"
              style={{ color: '#000000' }}
            >
              {t('contact', 'section_title')}
            </h2>
            <div className="w-16 h-1 mb-8 mx-auto" style={{ backgroundColor: '#000000' }}></div>
            <p 
              className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto"
              style={{ color: '#585856', fontWeight: 300, lineHeight: 1.7 }}
            >
              {t('contact', 'section_description')}
            </p>
          </div>
        </div>

        {/* Contact Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto mb-20 md:mb-24">
          
          {/* Email Card */}
          <div 
            className="transition-all duration-300 hover:shadow-lg"
            data-observe
            data-id="contact-email"
          >
            <div 
              className={`p-8 md:p-10 rounded-lg transition-all duration-700 ${
                visibleElements['contact-email'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                backgroundColor: '#F5F5F3',
                borderLeft: '3px solid #000000'
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Mail size={24} style={{ color: '#000000' }} />
                <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#585856' }}>
                  {t('contact', 'email_label')}
                </p>
              </div>
              <a 
                href="mailto:info@dhome-re.eu" 
                className="text-xl md:text-2xl font-semibold block mb-2 transition-colors duration-300"
                style={{ color: '#000000' }}
                onMouseEnter={(e) => e.target.style.color = '#585856'}
                onMouseLeave={(e) => e.target.style.color = '#000000'}
              >
                info@dhome-re.eu
              </a>
              <p className="text-xs md:text-sm" style={{ color: '#585856', fontWeight: 300 }}>
                {t('contact', 'email_subtitle')}
              </p>
            </div>
          </div>

          {/* Phone Card */}
          <div 
            className="transition-all duration-300 hover:shadow-lg"
            data-observe
            data-id="contact-phone"
          >
            <div 
              className={`p-8 md:p-10 rounded-lg transition-all duration-700 ${
                visibleElements['contact-phone'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                backgroundColor: '#F5F5F3',
                borderLeft: '3px solid #000000'
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Phone size={24} style={{ color: '#000000' }} />
                <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#585856' }}>
                  {t('contact', 'phone_label')}
                </p>
              </div>
              <a 
                href="tel:+393461343029" 
                className="text-xl md:text-2xl font-semibold block mb-2 transition-colors duration-300"
                style={{ color: '#000000' }}
                onMouseEnter={(e) => e.target.style.color = '#585856'}
                onMouseLeave={(e) => e.target.style.color = '#000000'}
              >
                +39 3461 343029
              </a>
              <p className="text-xs md:text-sm" style={{ color: '#585856', fontWeight: 300 }}>
                {t('contact', 'contact_note')}
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div 
          className="max-w-4xl mx-auto mb-20 md:mb-24"
          style={{ height: '1px', backgroundColor: '#E5E5E5' }}
        ></div>

        {/* FAQ Section */}
        <div 
          className="max-w-4xl mx-auto"
          data-observe
          data-id="faq-title"
        >
          <h3 
            className={`text-4xl md:text-5xl font-black leading-[1.1] tracking-tight mb-12 md:mb-16 transition-all duration-700 ${
              visibleElements['faq-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ color: '#000000' }}
          >
            {t('contact', 'faq_title')}
          </h3>

          <div className="space-y-0">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="transition-all duration-300"
                data-observe
                data-id={`faq-item-${index}`}
              >
                <div 
                  className={`transition-all duration-700 ${
                    visibleElements[`faq-item-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ borderBottom: '1px solid #E5E5E5' }}
                >
                  <button 
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between py-6 md:py-7 text-left transition-all duration-300 hover:px-4"
                    style={{ color: '#000000' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#585856'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#000000'}
                  >
                    <span className="font-semibold text-base md:text-lg pr-4">{faq.question}</span>
                    <ChevronDown 
                      size={20} 
                      className="flex-shrink-0 transition-transform duration-300"
                      style={{ 
                        transform: openFaqIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: '#000000'
                      }}
                    />
                  </button>

                  {/* Expandable content with smooth animation */}
                  {openFaqIndex === index && (
                    <div 
                      className="pb-6 pr-4 md:pr-6 animate-in"
                      style={{ color: '#585856', fontWeight: 300 }}
                    >
                      <p className="text-sm md:text-base leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;